import { addMinutes, setHours, setMinutes } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { Can, usePermanentRealtimeDealers } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, DMSIDS } from "models";
import { DeleteDmsDealerModal, DmsConfigSection, doesDealerDmsConfigExists } from "modules/DMSv3/Settings/components/AddEditDealerModal/components";
import { useDMSv3DealersListQuery } from "modules/DMSv3/Settings/components/DMSv3SettingsTable/hooks";
import { DMS_OPTIONS } from "utils/dms";

import type { DealerDMS } from "models";
import type { AddEditDealerFormPayload } from "modules/DMSv3/Settings/components/AddEditDealerModal";
import type { DeleteDmsDealerPayload } from "modules/DMSv3/Settings/components/AddEditDealerModal/hooks";

type AddEditDealerFormProps = {
  hasErrors: boolean;
  maxCheckInDaysPrior: number | null;
  isPending: boolean;
  selectedDealer: DealerDMS | undefined;
  onClose: () => void;
  onSubmit: (values: AddEditDealerFormPayload) => void;
};

const FormHelper = createFormHelper<AddEditDealerFormPayload>();

export const AddEditDealerForm = ({ isPending = false, selectedDealer, hasErrors, maxCheckInDaysPrior, onClose, onSubmit }: AddEditDealerFormProps) => {
  const { t } = useTranslation();

  const [deleteDmsDealerPayload, setDeleteDmsDealerPayload] = useState<DeleteDmsDealerPayload | null>(null);

  const { watch, handleSubmit, setValue } = FormHelper.useFormContext();
  const { dealers } = usePermanentRealtimeDealers();
  const { data: dmsDealersList = [] } = useDMSv3DealersListQuery();

  const [dealerID, timeToSleep, dateRange, startTime, endTime, durationThreshold, isIntervalFullRunEnabled, dmsId] = watch([
    "dealer_id",
    "time_to_sleep",
    "date_range",
    "start_time",
    "end_time",
    "duration_threshold",
    "is_interval_full_run_enabled",
    "dms_id"
  ]);

  const dealersOptions = dealers.map(dealer => ({ key: dealer.id, value: dealer.id, text: dealer.name })).sort((a, b) => a.text.localeCompare(b.text));

  const dmsDropdownOptions = DMS_OPTIONS.map(option => {
    const isDmsConfigured = dmsDealersList.some(d => d.dealer_id === dealerID && d.dms_id === option.value);
    return { ...option, disabled: isDmsConfigured, text: isDmsConfigured ? `${option.text} - ${t("v8_configured", "configured")}` : option.text };
  });

  const handleDealerChange = (dealerID?: number) => {
    setValue("dms_id", undefined);
    setValue("dealer_id", dealerID);
  };

  const handleDmsIdChange = (dmsID?: DMSIDS) => {
    setValue("dms_id", dmsID);

    if (dmsID) {
      if ([DMSIDS.AUTOLINE_EF_NEFKENS, DMSIDS.AUTOLINE_OPEL_MENU].includes(dmsID)) setValue("autoline_config.department_id", "W");
      else setValue("autoline_config.department_id", selectedDealer?.autoline_config?.department_id || "");
    }
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">{!selectedDealer ? t("v8_add_dealer", "Add Dealer") : `${t("v8_edit_dealer", "Edit Dealer")} ${selectedDealer.dealer_name}`}</div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Grid columns={16}>
          <GridColumn width={8}>
            <Form.Field required error={!dealerID && hasErrors}>
              <label>{t("v8_select_dealer", "Select Dealer")} </label>
              <FormHelper.Field.Dropdown
                name="dealer_id"
                options={dealersOptions}
                dropdownProps={{ fluid: true, search: true, selectOnBlur: false, scrolling: true, disabled: selectedDealer !== undefined }}
                onChange={handleDealerChange}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn width={8}>
            <Form.Field required error={!dmsId && hasErrors}>
              <label>{t("v8_select_dms", "Select DMS")} </label>
              <FormHelper.Field.Dropdown
                name="dms_id"
                options={dmsDropdownOptions}
                dropdownProps={{
                  fluid: true,
                  search: true,
                  selectOnBlur: false,
                  disabled: !dealerID || selectedDealer !== undefined,
                  scrolling: true
                }}
                onChange={handleDmsIdChange}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns={16}>
          <GridColumn width={8}>
            <Form.Field>
              <label>{t("v8_enter_dsn", "Enter DSN")} </label>
              <FormHelper.Field.Input name="dsn" inputProps={{ fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn width={4}>
            <Form.Field required error={(!timeToSleep || timeToSleep <= 0) && hasErrors}>
              <label>{t("v8_time_to_sleep", "Time to sleep")} (m) </label>
              <FormHelper.Field.Input name="time_to_sleep" inputProps={{ type: "number", fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn width={4}>
            <Form.Field required error={(!dateRange || maxCheckInDaysPrior !== null) && hasErrors}>
              <label>{t("v8_date_range_days", "Date range (days)")} </label>
              <FormHelper.Field.Input name="date_range" inputProps={{ type: "number", fluid: true }} />
              {maxCheckInDaysPrior !== null && (
                <div className="field-error">
                  {t("v8_date_range_must_be_higher_than_check_in_days_prior", "Date range must be higher than online check-in days prior ({{max}})", {
                    max: maxCheckInDaysPrior
                  })}
                </div>
              )}
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns={16}>
          <GridColumn width={3}>
            <Form.Field required error={!startTime && hasErrors}>
              <label>{t("v8_start_time", "Start Time")} </label>
              <FormHelper.Field.DatePickerRaw name="start_time" showTimeSelect showTimeSelectOnly dateFormat="HH:mm" timeFormat="HH:mm" />
            </Form.Field>
          </GridColumn>

          <GridColumn width={3}>
            <Form.Field required error={!endTime && hasErrors}>
              <label>{t("v8_end_time", "End Time")} </label>
              <FormHelper.Field.DatePickerRaw
                name="end_time"
                showTimeSelect
                showTimeSelectOnly
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                minTime={addMinutes(new Date(startTime), 30)}
                maxTime={setMinutes(setHours(new Date(), 23), 30)}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn width={4}>
            <Form.Field required error={!durationThreshold && hasErrors}>
              <label>{t("v8_duration_thresholds", "Duration threshold(s)")} </label>
              <FormHelper.Field.Input name="duration_threshold" inputProps={{ type: "number", fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn width={3}>
            <Form.Field>
              <label>{t("v8_enable_full_run_interval", "Enable Full Run Interval")} </label>
              <FormHelper.Field.Checkbox
                name="is_interval_full_run_enabled"
                checkboxProps={{ toggle: true }}
                onChange={value => !value && setValue("full_run_hour_interval", "")}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn width={3}>
            <Form.Field>
              <label>{t("v8_full_run_hour_interval", "Full Run Hour Interval")} </label>
              <FormHelper.Field.Input name="full_run_hour_interval" inputProps={{ fluid: true, type: "number", disabled: !isIntervalFullRunEnabled }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns={16}>
          <GridColumn width={3}>
            <Form.Field>
              <label>{t("v8_delay", "Delay")} </label>
              <FormHelper.Field.Input name="delay" inputProps={{ type: "number", fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn width={10}>
            <div className="checkboxes-container">
              <FormHelper.Field.Checkbox name="is_cron" label={t("v8_is_cron", "Is Cron")} />
              <FormHelper.Field.Checkbox name="import_date_created" label={t("v8_import_date_created", "Import Date Created")} />
              <FormHelper.Field.Checkbox name="is_custom_query_only" label={t("v8_custom_query_only", "Custom Query only")} />
            </div>
          </GridColumn>
        </Grid>

        {doesDealerDmsConfigExists(dmsId) && (
          <div className="dms-config-section">
            <Divider fitted />
            <DmsConfigSection dmsId={dmsId} />
          </div>
        )}

        {selectedDealer && (
          <h4>
            {t("v8_dmsv3_key", "DMSv3 key")} : {selectedDealer.dmsv3_key}
          </h4>
        )}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {selectedDealer && (
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.DMSv3SettingDealers}>
              <Form.Button
                color="red"
                type="button"
                floated="left"
                onClick={() => setDeleteDmsDealerPayload({ dealer_id: selectedDealer.dealer_id, dms_id: selectedDealer.dms_id })}
              >
                {t("v8_delete") || "Delete"}
                <Icon className="trash" />
              </Form.Button>
            </Can>
          )}

          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Can I={selectedDealer ? ACL_ACTIVITIES.Update : ACL_ACTIVITIES.Create} the={ACL_RESOURCES.DMSv3SettingDealers}>
              <Form.Button type="button" color="green" disabled={isPending} loading={isPending} onClick={handleSubmit(onSubmit)}>
                {!selectedDealer ? t("v8_save", "Save") : t("v8_update", "Update")}
                <Icon className="check" />
              </Form.Button>
            </Can>
          </div>
        </div>
      </Modal.Actions>

      <DeleteDmsDealerModal deleteDmsDealerPayload={deleteDmsDealerPayload} onCancel={() => setDeleteDmsDealerPayload(null)} onClose={onClose} />
    </>
  );
};
